<script>
import { createNamespacedHelpers } from 'vuex';
import {
  ScTag,
  ScCard,
  ScForm,
  ScField,
  ScSelect,
  ScDatepicker,
  ScSimpleUpload,
  ScButton,
  ScToast,
  ScWarehouseDropdown,
} from '@intelipost-js/secure-components';
import { Lodash } from '@intelipost-js/secure-providers';
import NewPageBase from '../../../../components/new-page-base/NewPageBase.vue';
import BillingLoadingModal from '../../components/billing-loading-modal/BillingLoadingModal.vue';
import BillingUploadSavingModal from './components/BillingUploadSavingModal.vue';

const { mapState, mapActions } = createNamespacedHelpers('recon/billing');

export default {
  name: 'PageBillingUpload',
  data() {
    return {
      loadingLogisticProviders: false,
      formContent: {
        templateId: null,
        billingWarehouseId: null,
        billingFile: null,
        billingDueDate: null,
        overwriteId: this.$route.query.overwrite,
      },
    };
  },
  methods: {
    ...mapActions(['loadClientTemplates', 'uploadBillingFile']),
    submitUpload(formContent) {
      this.formContent = { ...formContent };
      this.uploadBillingFile(this.formContent);
    },
  },
  computed: {
    ...mapState(['clientBillingTemplates', 'loadingBillingTemplates']),
    loadingTemplateOptions() {
      return this.loadingBillingTemplates || this.$store.state.logisticProviders.loading;
    },
    templateOptions() {
      const deliveryMethods = this.$store.getters['logisticProviders/allDeliveryMethods'];
      if (Lodash.isEmpty(this.clientBillingTemplates) || Lodash.isEmpty(deliveryMethods)) return [];

      return this.clientBillingTemplates.map((template) => {
        const deliveryMethod = deliveryMethods.find(dm => dm.id === template.deliveryMethodId);
        return { ...template, deliveryMethod };
      });
    },
    rules() {
      return {
        templateId: [{ required: true, message: 'Selecione o modelo da fatura' }],
        billingFile: [{ required: true, message: 'Selecione o arquivo da fatura' }],
        billingDueDate: [{ required: true, message: 'Selecione a data de vencimento da fatura' }],
      };
    },
  },
  async created() {
    this.loadClientTemplates();
    if (Lodash.isEmpty(this.$store.getters['logisticProviders/allDeliveryMethods'])) {
      await this.$store.dispatch('logisticProviders/getLogisticProviders', false);
    }
  },
  components: {
    BillingUploadSavingModal,
    ScTag,
    ScCard,
    ScForm,
    ScField,
    ScSelect,
    ScDatepicker,
    ScSimpleUpload,
    ScButton,
    ScToast,
    ScWarehouseDropdown,
    NewPageBase,
    BillingLoadingModal,
  },
};
</script>

<template>
  <new-page-base
    @back="$router.push({ name: 'billing-main-page' })"
    showBackText="Faturas">

    <template slot="titleLeft">
      Adicionar fatura
    </template>

    <template slot="subtitleSection">
      Por aqui você pode fazer o upload da sua fatura manualmente e visualizar o histórico dos arquivos enviados anteriormente.
    </template>

    <section class="page-content">
      <sc-card class="page-content__upload-card">
        <template slot="body">
          <sc-form :has-style="false" :form="formContent" :rules="rules" @submit="submitUpload">
            <template slot="fields" slot-scope="{ fields }">
              <section class="upload-form">
                <sc-field id="billing-step-1" class="upload-form__step" prop="templateId">
                  <template slot="label">
                    <span class="upload-form__step__label">
                      <sc-tag size="is-medium" rounded>1</sc-tag>
                      Transportadora
                    </span>
                  </template>
                  <sc-select
                    size="is-medium"
                    v-model="fields.templateId"
                    placeholder="Transportadora"
                    :loading="loadingTemplateOptions"
                    :disabled="loadingTemplateOptions"
                    expanded>

                    <option v-for="template in templateOptions" :key="template.id" :value="template.id">
                      {{ template.deliveryMethod.logistic_provider_name }} - {{ template.deliveryMethod.name }}
                    </option>
                  </sc-select>
                </sc-field>
                <sc-field id="billing-step-1.1" class="upload-form__step" prop="warehouse">
                  <template slot="label">
                    <span class="upload-form__step__label">
                      <sc-tag size="is-medium" rounded>2</sc-tag>
                      Centro de distribuição
                    </span>
                  </template>
                  <sc-warehouse-dropdown
                    size="is-medium"
                    v-model="fields.billingWarehouseId"
                    placeholder="Centro de distribuição"
                    list-user
                    expanded
                  />
                </sc-field>
              </section>
              <section class="upload-form">
                <sc-field id="billing-step-2" class="upload-form__step" prop="billingFile">
                  <template slot="label">
                      <span class="upload-form__step__label">
                        <sc-tag size="is-medium" rounded>3</sc-tag>
                        Upload do arquivo da fatura
                      </span>
                  </template>
                  <sc-simple-upload
                    v-model="fields.billingFile"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    expanded drag-drop>
                    <a class="button is-fullwidth is-medium">
                        <span v-if="fields.billingFile">
                          <sc-tag class="selected-file" size="is-medium">
                            {{ fields.billingFile.name }}
                            <button class="delete is-small" type="button" @click="() => fields.billingFile = null"/>
                          </sc-tag>
                        </span>
                      <span v-else>
                          <i class="fas fa-upload" style="margin-right: .5em"></i>
                          Arraste um arquivo ou <strong>clique</strong> para buscar
                        </span>
                    </a>
                  </sc-simple-upload>
                </sc-field>
                <sc-field id="billing-step-3" class="upload-form__step" prop="billingDueDate">
                  <template slot="label">
                      <span class="upload-form__step__label">
                        <sc-tag size="is-medium" rounded>4</sc-tag>
                        Data de vencimento
                      </span>
                  </template>
                  <sc-datepicker v-model="fields.billingDueDate" placeholder="Selecione uma data" size="is-medium"/>
                </sc-field>
              </section>
            </template>
            <template slot="actions" slot-scope="{ isValid }">
              <section class="continue-buttom">
                <sc-button theme="primary" type="submit" text="Continuar"/>
              </section>
            </template>
            </sc-form>
        </template>
      </sc-card>
      <sc-card class="page-content__template-warning">
        <template slot="body">
          <section class="content">
            <div class="content__message">
              <i class="fas fa-info-circle warning-icon"></i>
              <div>
                <div><strong>Não encontrou a transportadora que você precisa?</strong></div>
                <div>Crie um novo modelo de fatura e identifique os valores divergentes da fatura de uma nova transportadora..</div>
              </div>
            </div>
            <router-link class="button" :to="{ name: 'create-billing-template' }">
              Criar novo modelo
            </router-link>
          </section>
        </template>
      </sc-card>
    </section>

    <billing-upload-saving-modal
      @onDuplicateBilling="submitUpload({ ...formContent, onDuplicateAction: 'ADD' })"
      @onOverwriteBilling="submitUpload({ ...formContent, onDuplicateAction: 'REPLACE' })"/>
  </new-page-base>
</template>

<style scoped lang="scss">
@import "~@intelipost-js/secure-ui/src/scss/variables"; //eslint-disable-line
.page-content {
  margin-top: 1.5em;

  &__upload-card {
    border-radius: 8px;

    .upload-form {

      display: flex;
      justify-content: space-between;
      margin-left: -0.75em;
      margin-right: -0.75em;

      &__step {
        flex: 1;
        margin: 0.75em;

        &__label {
          color: $primary;

          .tag {
            color: #fff;
            background-color: $primary;
          }
        }
      }
    }

    .continue-buttom {
      margin-top: 1.5em;
      display: flex;
      justify-content: flex-end;

      .button {
        font-weight: 700;
      }
    }
  }

  &__template-warning {
    margin-top: 40px;
    border-style: solid;
    border-width: 1px;
    border-radius: 8px;
    border-color: #3670D6;

    .content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &__message {
        display: flex;
        align-items: baseline;

        .warning-icon {
          margin: 5px;
          color: #3670D6;
        }
      }

      .button {
        font-weight: 700;
      }
    }
  }
}
</style>

<style lang="scss">
@import "~@intelipost-js/secure-ui/src/scss/variables"; //eslint-disable-line
#billing-step-2 {
  .upload .upload-draggable {
    padding: 0;
    border: none;

    &.is-hovered {
      border: 1px dashed $grey-light;
      background: rgba($grey-light, 0.5);
    }

    .button {
      justify-content: left !important;

      .selected-file > span {
        display: flex;
        align-items: center;
      }
    }

  }
}

</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/billing/pages/upload/PageBillingUpload.vue?2afbc472